import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Search, Trash2, ChevronDown, ChevronUp, UserPlus, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { Select as ShadSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const ProviderPool = () => {
  const { token } = useAuth();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPools, setExpandedPools] = useState({});
  
  // Create pool dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newServiceType, setNewServiceType] = useState('');
  const [newPoolName, setNewPoolName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Provider selection
  const [allProviders, setAllProviders] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProviders, setFilteredProviders] = useState([]);
  
  // Add to pool dialog
  const [showAddToPoolDialog, setShowAddToPoolDialog] = useState(false);
  const [selectedProviderToAdd, setSelectedProviderToAdd] = useState(null);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [adding, setAdding] = useState(false);
  
  // Assign lead dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Service types loaded from API
  const [serviceTypes, setServiceTypes] = useState([]);
  const [provPage, setProvPage] = useState(1);
  const [provPageSize, setProvPageSize] = useState(10);

  useEffect(() => {
    fetchPools();
    fetchLeads();
    fetchServiceTypes();
    fetchAllProviders();
  }, []);

  const fetchAllProviders = async () => {
    try {
      const response = await axios.get(`${API}/api/agent/service-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllProviders(response.data);
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  useEffect(() => {
    // Filter providers when service type or search query changes
    let filtered = allProviders;
    
    // Filter by service type
    if (selectedServiceType) {
      filtered = filtered.filter(p => {
        // Check if service type is in provider's service_types array (for multi-service providers)
        if (p.service_types && Array.isArray(p.service_types)) {
          return p.service_types.includes(selectedServiceType);
        }
        // Fallback to single service_type string for backward compatibility
        return p.service_type === selectedServiceType;
      });
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(provider =>
        (provider.full_name || provider.name || '').toLowerCase().includes(query) ||
        (provider.company_name || '').toLowerCase().includes(query) ||
        (provider.email || '').toLowerCase().includes(query) ||
        (provider.city || '').toLowerCase().includes(query) ||
        (provider.state || '').toLowerCase().includes(query)
      );
    }
    
    setFilteredProviders(selectedServiceType || searchQuery.trim() ? filtered : []);
    setProvPage(1);
  }, [selectedServiceType, searchQuery, allProviders]);

  const fetchPools = async () => {
    try {
      const response = await axios.get(`${API}/api/provider-pools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPools(response.data);
    } catch (error) {
      console.error('Failed to load provider pools:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load provider pools');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to load leads', error);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/provider-pools/service-types`);
      setServiceTypes(response.data.service_types || []);
    } catch (error) {
      console.error('Failed to load service types', error);
      // Fallback to defaults if API fails
      setServiceTypes([
        'Handyman',
        'Attorney',
        'Home Inspector',
        'Mortgage Broker',
        'Title Company',
        'Moving Company',
        'Cleaning Service',
        'Landscaper',
        'Electrician',
        'Plumber',
        'HVAC',
        'Roofer',
        'Painter',
        'Photographer',
        'Stager'
      ]);
    }
  };

  const handleCreatePool = async (e) => {
    e.preventDefault();
    if (!newServiceType.trim() || !newPoolName.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/api/provider-pools`,
        { 
          service_type: newServiceType,
          pool_name: newPoolName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Pool "${newPoolName}" created successfully!`);
      setShowCreateDialog(false);
      setNewServiceType('');
      setNewPoolName('');
      fetchPools();
    } catch (error) {
      console.error('Pool creation error:', error.response || error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create pool');
    } finally {
      setCreating(false);
    }
  };

  const handleServiceTypeChange = (value) => {
    setNewServiceType(value);
    // Auto-populate pool name with default pattern
    if (value) {
      setNewPoolName(`${value} Pool`);
    }
  };

  const handleTogglePoolStatus = async (poolId, currentStatus, poolName) => {
    const newStatus = !currentStatus;
    
    try {
      await axios.put(
        `${API}/api/provider-pools/${poolId}/status`,
        { active: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Pool "${poolName}" set to ${newStatus ? 'active' : 'inactive'}`);
      fetchPools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pool status');
    }
  };

  const handleAddProvider = async (e) => {
    e.preventDefault();
    if (!selectedPoolId) {
      toast.error('Please select a pool');
      return;
    }

    setAdding(true);
    try {
      await axios.put(
        `${API}/api/provider-pools/${selectedPoolId}/add-provider`,
        { provider_id: selectedProviderToAdd.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Provider added to pool!');
      setShowAddToPoolDialog(false);
      setSelectedProviderToAdd(null);
      setSelectedPoolId('');
      fetchPools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add provider');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveProvider = async (poolId, providerId, poolName) => {
    if (!window.confirm(`Remove this provider from ${poolName}?`)) return;

    try {
      await axios.delete(`${API}/api/provider-pools/${poolId}/remove-provider/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Provider removed from pool');
      fetchPools();
    } catch (error) {
      toast.error('Failed to remove provider');
    }
  };

  const handleAssignLead = async (e) => {
    e.preventDefault();
    if (!selectedLead) {
      toast.error('Please select a lead');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(
        `${API}/api/provider-pools/${selectedPool.pool_id}/assign-lead`,
        { lead_id: selectedLead },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const count = response.data.assignments?.length || response.data.message.match(/\d+/)?.[0] || selectedPool.providers.length;
      toast.success(`Lead assigned to ${count} provider(s) successfully!`);
      setShowAssignDialog(false);
      setSelectedLead('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const togglePoolExpansion = (poolId) => {
    setExpandedPools(prev => ({
      ...prev,
      [poolId]: !prev[poolId]
    }));
  };

  const openAddToPoolDialog = (provider) => {
    setSelectedProviderToAdd(provider);
    setShowAddToPoolDialog(true);
    setSelectedPoolId('');
  };

  const openAssignDialog = (pool) => {
    if (!pool.providers || pool.providers.length === 0) {
      toast.error('This pool has no providers. Add providers first.');
      return;
    }
    setSelectedPool(pool);
    setSelectedLead('');
    setShowAssignDialog(true);
  };

  // Get eligible pools for the selected provider (matching service type)
  const getEligiblePools = () => {
    if (!selectedProviderToAdd) return [];
    
    return pools.filter(pool => {
      // Check if pool's service type matches any of provider's service types
      const providerServiceTypes = selectedProviderToAdd.service_types || [selectedProviderToAdd.service_type];
      const poolMatches = providerServiceTypes.includes(pool.service_type);
      const notAlreadyInPool = !pool.provider_ids.includes(selectedProviderToAdd.id);
      
      return poolMatches && notAlreadyInPool;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-stone-500">Loading provider pools...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <Users size={28} className="text-[#1A4D2E] sm:hidden" />
            <Users size={32} className="text-[#1A4D2E] hidden sm:block" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Provider Pools</h1>
              <p className="text-sm sm:text-base text-stone-600">Organize and manage your service providers</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="w-full sm:w-auto bg-[#1A4D2E] hover:bg-[#143D24] text-white h-12"
          >
            <Plus size={20} className="mr-2" />
            Create Pool
          </Button>
        </div>

        {/* Select Provider Section */}
        <Card className="p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
            <Users size={20} className="sm:hidden" />
            <Users size={24} className="hidden sm:block" />
            Select Provider
          </h2>
          
          {/* Filters - Service Type and Search */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Service Type Dropdown */}
            <div>
              <Label className="mb-2 block">Service Type</Label>
              <Select
                value={selectedServiceType}
                onChange={(e) => setSelectedServiceType(e.target.value)}
                options={serviceTypes}
                placeholder="Select service type..."
                className="w-full"
              />
            </div>
            
            {/* Search Field */}
            <div>
              <Label className="mb-2 block">Search Provider</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={20} />
                <Input
                  placeholder="Search by name, company, email, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                />
              </div>
            </div>
          </div>

          {/* Provider List */}
          {filteredProviders.length > 0 && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredProviders.length / provPageSize));
            const paginated = filteredProviders.slice((provPage - 1) * provPageSize, provPage * provPageSize);
            return (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-stone-900">
                  Showing {Math.min((provPage - 1) * provPageSize + 1, filteredProviders.length)}-{Math.min(provPage * provPageSize, filteredProviders.length)} of {filteredProviders.length} providers
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-stone-500">Per page:</span>
                    <ShadSelect value={String(provPageSize)} onValueChange={(v) => { setProvPageSize(Number(v)); setProvPage(1); }}>
                      <SelectTrigger className="w-[70px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="30">30</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </ShadSelect>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={provPage <= 1} onClick={() => setProvPage(p => p - 1)}>
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-xs font-medium text-stone-700 px-2">{provPage}/{totalPages}</span>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={provPage >= totalPages} onClick={() => setProvPage(p => p + 1)}>
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginated.map((provider) => (
                  <Card key={provider.id} className="p-4 border-2 hover:border-[#1A4D2E] transition-colors">
                    <div className="mb-3">
                      <h4 className="font-bold text-stone-900">{provider.full_name || provider.name}</h4>
                      <p className="text-sm text-stone-600">{provider.service_type}</p>
                    </div>
                    <div className="space-y-1 text-sm text-stone-600 mb-3">
                      <p>{provider.email}</p>
                      {provider.phone && <p>{formatPhoneDisplay(provider.phone)}</p>}
                    </div>
                    <Button
                      onClick={() => openAddToPoolDialog(provider)}
                      size="sm"
                      className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                    >
                      <Plus size={14} className="mr-1" />
                      Add to Pool
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
            );
          })()}

          {(selectedServiceType || searchQuery.trim()) && filteredProviders.length === 0 && (
            <div className="text-center py-6 text-stone-500">
              <p>No providers found matching your criteria</p>
              <p className="text-sm mt-1">Try adjusting your filters or search query</p>
            </div>
          )}
        </Card>

        {/* Pools List */}
        {pools.length === 0 ? (
          <Card className="p-12 text-center">
            <Users size={64} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No Provider Pools Yet</h3>
            <p className="text-stone-600 mb-6">Create your first pool to start organizing service providers</p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
            >
              <Plus size={20} className="mr-2" />
              Create Your First Pool
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {pools.map((pool) => (
              <Card key={pool.pool_id} className="overflow-hidden">
                {/* Pool Header */}
                <div
                  className="flex items-center justify-between p-4 sm:p-6 bg-stone-50 hover:bg-stone-100 transition-colors"
                >
                  <div 
                    className="flex items-center gap-2 sm:gap-4 flex-1 cursor-pointer min-w-0"
                    onClick={() => togglePoolExpansion(pool.pool_id)}
                  >
                    <div className={`${pool.active !== false ? 'bg-[#1A4D2E]' : 'bg-stone-400'} text-white p-2 sm:p-3 rounded-lg transition-colors flex-shrink-0`}>
                      <Users size={20} className="sm:hidden" />
                      <Users size={24} className="hidden sm:block" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base sm:text-xl font-bold text-stone-900 truncate">{pool.pool_name}</h3>
                        {pool.active === false && (
                          <span className="px-2 py-1 text-xs font-semibold bg-stone-200 text-stone-600 rounded flex-shrink-0">
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-stone-600 truncate">
                        {pool.providers?.length || 0} provider(s) • {pool.service_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Label htmlFor={`pool-status-${pool.pool_id}`} className="text-sm text-stone-600 cursor-pointer hidden sm:block">
                        {pool.active !== false ? 'Active' : 'Inactive'}
                      </Label>
                      <Switch
                        id={`pool-status-${pool.pool_id}`}
                        checked={pool.active !== false}
                        onCheckedChange={() => handleTogglePoolStatus(pool.pool_id, pool.active !== false, pool.pool_name)}
                        className="data-[state=checked]:bg-[#1A4D2E]"
                      />
                    </div>
                    {pool.providers && pool.providers.length > 0 && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssignDialog(pool);
                        }}
                        variant="outline"
                        size="sm"
                        className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white whitespace-nowrap"
                      >
                        <Send size={16} className="sm:mr-1" />
                        <span className="hidden sm:inline">Assign Lead</span>
                      </Button>
                    )}
                    <div className="cursor-pointer" onClick={() => togglePoolExpansion(pool.pool_id)}>
                      {expandedPools[pool.pool_id] ? (
                        <ChevronUp size={20} className="text-stone-400 sm:hidden" />
                      ) : (
                        <ChevronDown size={20} className="text-stone-400 sm:hidden" />
                      )}
                      {expandedPools[pool.pool_id] ? (
                        <ChevronUp size={24} className="text-stone-400 hidden sm:block" />
                      ) : (
                        <ChevronDown size={24} className="text-stone-400 hidden sm:block" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Pool Content (Expandable) */}
                {expandedPools[pool.pool_id] && (
                  <div className="p-6">
                    {pool.providers && pool.providers.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {pool.providers.map((provider) => (
                          <Card key={provider.id} className="p-4 border-2 hover:border-[#1A4D2E] transition-colors">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-bold text-stone-900">{provider.full_name || provider.name}</h4>
                                <p className="text-sm text-stone-600">{provider.service_type}</p>
                              </div>
                              <Button
                                onClick={() => handleRemoveProvider(pool.pool_id, provider.id, pool.pool_name)}
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                            <div className="space-y-1 text-sm text-stone-600">
                              <p>{provider.email}</p>
                              {provider.phone && <p>{formatPhoneDisplay(provider.phone)}</p>}
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-stone-500">
                        <p>No providers in this pool yet</p>
                        <p className="text-sm mt-2">Search for providers above and add them to this pool</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Create Pool Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Provider Pool</DialogTitle>
              <DialogDescription>
                Create a pool to organize service providers by type
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreatePool} className="space-y-4">
              <div>
                <Label>Service Type</Label>
                <Select
                  value={newServiceType}
                  onChange={(e) => handleServiceTypeChange(e.target.value)}
                  options={serviceTypes}
                  placeholder="Select service type..."
                  required
                />
              </div>
              <div>
                <Label>Pool Name</Label>
                <Input
                  value={newPoolName}
                  onChange={(e) => setNewPoolName(e.target.value)}
                  placeholder="e.g., Electrician Pool, My Handyman Team"
                  required
                />
                <p className="text-xs text-stone-500 mt-1">
                  Default: {newServiceType ? `${newServiceType} Pool` : 'Auto-fills based on service type'}
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} className="bg-[#1A4D2E] hover:bg-[#143D24] text-white">
                  {creating ? 'Creating...' : 'Create Pool'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add to Pool Dialog */}
        <Dialog open={showAddToPoolDialog} onOpenChange={setShowAddToPoolDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Provider to Pool</DialogTitle>
              <DialogDescription>
                Select a pool to add {selectedProviderToAdd?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddProvider} className="space-y-4">
              <div>
                <Label>Select Pool</Label>
                <Select
                  value={selectedPoolId}
                  onChange={(e) => setSelectedPoolId(e.target.value)}
                  options={getEligiblePools().map(pool => ({
                    value: pool.pool_id,
                    label: `${pool.pool_name} (${pool.providers?.length || 0} providers)`
                  }))}
                  placeholder="Choose a pool..."
                  required
                />
                {getEligiblePools().length === 0 && selectedProviderToAdd && (
                  <p className="text-xs text-orange-600 mt-2">
                    No eligible pools found. Create a pool for one of this provider's service types: {
                      (selectedProviderToAdd.service_types || [selectedProviderToAdd.service_type])
                        .map(type => `"${type}"`)
                        .join(', ')
                    }
                  </p>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAddToPoolDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={adding || getEligiblePools().length === 0} 
                  className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                >
                  {adding ? 'Adding...' : 'Add to Pool'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Lead Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Lead to All Providers</DialogTitle>
              <DialogDescription>
                This lead will be assigned to all {selectedPool?.providers?.length || 0} provider(s) in "{selectedPool?.pool_name}"
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAssignLead} className="space-y-4">
              <div>
                <Label>Select Lead</Label>
                <Select
                  value={selectedLead}
                  onChange={(e) => setSelectedLead(e.target.value)}
                  options={leads.map(lead => ({
                    value: lead.id,
                    label: `${lead.name} - ${lead.email}`
                  }))}
                  placeholder="Choose a lead..."
                  required
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={assigning} className="bg-[#1A4D2E] hover:bg-[#143D24] text-white">
                  {assigning ? 'Assigning...' : `Assign to All ${selectedPool?.providers?.length || 0} Providers`}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProviderPool;
